// Production image URL normalization: rewrite CDN/delivery URLs onto the full-original variant.

import { CONFIG } from '../../../core/config.js'

/** Cloudflare Images delivery host (direct imagedelivery.net URLs). */
export const CF_IMAGES_DOMAIN = 'imagedelivery.net'

// Production-resolution contract (CORRECTED 2026-06-06): the image added to
// the SDS canvas IS the production material — SDS stores exactly what we
// inject and prints from it. The CF Images `public` variant is a DOWNSCALED
// small image (~432x768), NOT the original — injecting it produced finished
// products SDS rejected as material-resolution-insufficient, which surfaced as
// a misleading finished-product-id-required error at order time. The original
// at its true pixel size is only reachable via the `w=4000,fit=scale-down`
// flexible variant (returns the full original, e.g. 1440x2560, never upscaled
// past source). So the canvas MUST receive that, never `public`.
// (Low-res w=200 is only for gallery/material browsing — handled separately.)
const PRODUCTION_VARIANT = 'w=4000,fit=scale-down,format=auto'

const LIVE_DELIVERY_BASE = CONFIG.IMAGE_CDN

/**
 * Rewrite a CDN/delivery URL onto the full-original production variant
 * (and rescue retired images-api.mamamiya.store hosts). Non-CDN URLs pass
 * through untouched.
 *
 * @param {string} rawUrl
 * @returns {string}
 */
export function toProductionVariant(rawUrl) {
  try {
    let u = new URL(rawUrl)
    // Retired-host rescue: localStorage "recently used" entries (and any
    // other stale caches) may still carry images-api.mamamiya.store URLs —
    // that DNS is deleted, every fetch dies. Rebuild on the live delivery
    // base from the embedded cloudflare id (path: /cdn/<id>/<variant>).
    if (u.hostname === 'images-api.mamamiya.store') {
      const parts = u.pathname.split('/').filter(Boolean) // ['cdn', id, variant]
      if (parts.length >= 2) {
        // Always rebuild on the full-original variant — `public` is the small
        // downscaled image and must never be used for production material.
        u = new URL(`${LIVE_DELIVERY_BASE}/${parts[1]}/${PRODUCTION_VARIANT}`)
      }
    }
    const isOurCdn =
      u.hostname === 'cdn.mamamiya.store' && u.pathname.includes('/cdn-cgi/imagedelivery/')
    const isDelivery = u.hostname === CF_IMAGES_DOMAIN
    if (!isOurCdn && !isDelivery) return rawUrl
    // Path shape: .../imagedelivery/<hash>/<id>/<variant> (ours) or
    // /<hash>/<id>/<variant> (imagedelivery.net) — swap the variant segment.
    const parts = u.pathname.split('/')
    if (parts.length < 2) return rawUrl
    parts[parts.length - 1] = PRODUCTION_VARIANT
    u.pathname = parts.join('/')
    return u.toString()
  } catch {
    return rawUrl
  }
}
